import { AttachMoney, Edit, MoreHoriz, PlayArrow } from '@mui/icons-material';
import { Box, Chip, useMediaQuery, useTheme } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButtonLink } from '~/components/button';
import { hiddenHeader } from '~/components/data-grid';
import { FolderIcon } from '~/components/folder-breadcrumbs/styles';
import { formatRuntime } from '~/lib/runtime';
import type {
  PlaylistList__PlaylistFolder as Folder,
  PlaylistList__Playlist as Playlist,
} from '../../queries/queries.generated';
import { Menu, PlaylistThumbnail } from '../components';

type PlaylistOrFolder = Playlist | Folder;

export const useColumns = (): GridColDef<PlaylistOrFolder>[] => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallAndUp = useMediaQuery(theme.breakpoints.up('sm'));

  return useMemo(() => {
    const columns: GridColDef<PlaylistOrFolder>[] = [
      {
        field: 'thumbnailUri',
        ...hiddenHeader(t('common.labels.thumbnail')),
        width: 84,
        resizable: false,
        sortable: false,
        renderCell: ({ row }) => {
          if (row.__typename === 'PlaylistFolder') {
            return (
              <Box display="flex" alignItems="center" justifyContent="center" width="100%">
                <FolderIcon />
              </Box>
            );
          }
          return (
            <Box display="flex" alignItems="center" justifyContent="center" width="100%">
              <PlaylistThumbnail size="small" />
            </Box>
          );
        },
      },
      {
        field: 'name',
        flex: 1,
        headerName: t('common.labels.name'),
        minWidth: 150,
      },
      {
        field: 'kind',
        headerName: t('pages.playlists.columns.kind'),
        sortable: false,
        minWidth: 150,
        renderCell: ({ row }) => {
          if (row.__typename === 'PlaylistFolder') return '--';
          return row.kind === 'ad' ? (
            <Chip
              icon={<AttachMoney />}
              label={t('pages.playlists.columns.sponsor')}
              color="success"
            />
          ) : null;
        },
      },
      {
        field: 'playlistCount',
        headerName: t('common.pluralize.playlist_plural', { count: 2 }),
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        minWidth: 100,
        renderCell: ({ row }) => {
          if (row.__typename === 'PlaylistFolder') {
            return <Chip color="primary" label={row.itemCount || 0} />;
          }
          return '--';
        },
      },
      {
        field: 'showCount',
        headerName: t('common.tabs.shows'),
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        minWidth: 100,
        renderCell: ({ row }) => {
          if (row.__typename === 'PlaylistFolder') return '--';
          return <Chip color="primary" label={row.showCount} />;
        },
      },
      {
        field: 'playlistItemsCount',
        headerName: t('pages.playlists.columns.items'),
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        minWidth: 100,
        renderCell: ({ row }) => {
          if (row.__typename === 'PlaylistFolder') return '--';
          return <Chip color="primary" label={row.playlistItemsCount} />;
        },
      },
      {
        field: 'runtime',
        headerName: t('common.labels.duration'),
        sortable: false,
        minWidth: 150,
        renderCell: ({ row }) => {
          if (row.__typename === 'PlaylistFolder') return '--';
          return formatRuntime(row.runtime);
        },
      },
      {
        field: 'actions',
        type: 'actions',
        align: 'right',
        minWidth: isSmallAndUp ? 150 : 0,
        getActions: ({ row }) => {
          if (row.__typename === 'PlaylistFolder') {
            return [
              <Menu key={`more-${row.id}`} item={row} Icon={MoreHoriz} buttonType="action" />,
            ];
          }

          return [
            <ActionButtonLink
              key={`edit-${row.id}`}
              title={t('common.actions.edit')}
              Icon={Edit}
              to={`playlist/${row.id}/design`}
            />,
            <ActionButtonLink
              key={`preview-${row.id}`}
              title={t('common.labels.preview')}
              Icon={PlayArrow}
              to={`playlist/${row.id}/preview`}
            />,
            <Menu key={`more-action-${row.id}`} item={row} Icon={MoreHoriz} buttonType="action" />,
          ];
        },
      },
    ];

    return columns;
  }, [isSmallAndUp, t]);
};
